import { ref, computed, onMounted, onBeforeUnmount, toValue } from 'vue'

const SCRIPT_BASE = 'https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id='

/**
 * Manages Frontrow Health sticker widget lifecycle:
 * - reactive iframe src
 * - script injection / cleanup
 * - listens to `lifeboost:step1-product-change` to swap product
 *
 * @param {object} options
 * @param {Ref<string>|string} options.defaultFrontrowId  - initial product id
 * @param {Ref<string>|string} options.frontrowProductMap - JSON string mapping product names → ids
 * @param {Ref<string>|string} [options.iframeId]         - id of the sticker iframe element
 * @param {Ref<boolean>|boolean} [options.enabled=true]   - skip mounting when false
 */
export function useFrontrowSticker({ defaultFrontrowId, frontrowProductMap, iframeId, enabled = true }) {
  const currentFrontrowId = ref(toValue(defaultFrontrowId))

  const frontrowIframeSrc = computed(() =>
    `https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=${currentFrontrowId.value}`
  )

  const loadFrontrowScript = (productId) => {
    if (!import.meta.client) return
    const id = toValue(iframeId)
    const iframe = id ? document.getElementById(id) : null
    const container = iframe?.parentElement ?? document.head
    const oldScript = container.querySelector(`script[src^="${SCRIPT_BASE}"]`)
    if (oldScript) oldScript.remove()
    const script = document.createElement('script')
    script.defer = true
    script.src = SCRIPT_BASE + productId
    if (iframe) {
      container.insertBefore(script, iframe)
    } else {
      container.appendChild(script)
    }
  }

  const handleStep1Change = (e) => {
    const name = e?.detail?.name
    let map = {}
    try {
      map = JSON.parse(toValue(frontrowProductMap) || '{}')
    } catch {
      map = {}
    }
    const id = (name && map[name]) ? map[name] : toValue(defaultFrontrowId)
    currentFrontrowId.value = id
    loadFrontrowScript(id)
  }

  onMounted(() => {
    if (!toValue(enabled)) return
    loadFrontrowScript(currentFrontrowId.value)
    window.addEventListener('lifeboost:step1-product-change', handleStep1Change)
  })

  onBeforeUnmount(() => {
    if (!import.meta.client) return
    window.removeEventListener('lifeboost:step1-product-change', handleStep1Change)
  })

  return { currentFrontrowId, frontrowIframeSrc }
}
